import React, { useEffect, useCallback } from "react";
// https://github.com/facebook/react/issues/10780
import GoogleMapReact from "google-map-react";

import { useAppContext } from "./helpers/account-context";

import {
  ICONS,
  processLocations,
  sortNearbyLocations
} from "./helpers/helper";
import { lookupLocations, CBAnalytics } from "./helpers/apiEndpoints";
import {
  requestGeocode,
  syncLocationStates,
} from "./helpers/sharedUtils";
import { GMarker, ClusterMarker, GeoMarker } from "./Maps/Gmaps";
import { ListResults } from "./Maps/Results";
import LocationDialog from "./Maps/LocationDialog";
import FilterDialog from "./Maps/FilterDialog";
import SearchOuter from "./Maps/SearchOuter";
import SearchInner from "./Maps/SearchInner";
import LeadForm from "./Maps/LeadForm";
import { Loading } from "./Maps/Components/Mixin";
import TaggableFilters from "./Maps/TaggableFilters";
import { useMixMap } from "./helpers/useMixMap";

const CLUSTER_MAX_ZOOM = 14;

const isInBounds = (bounds, { properties: { lat, lng } }) => {
  return (
    lng >= bounds.nw_lng &&
    lng <= bounds.se_lng &&
    lat >= bounds.nw_lat &&
    lat <= bounds.se_lat
  );
};

const detectPointsInBounds = ({ bounds }, points) => {
  if (!bounds) return false;
  return points.find((point) => isInBounds(bounds, point));
};

const appendClassName = (account, initedData) => {
  const {
    mapTheme,
    filterLayout,
    tagsLayout,
    filtersPosition,
    contentPositionDesktop,
    contentPositionMobile,
  } = initedData;
  const themePrefix = "cbmap-theme";
  return `cbmap-widget
    ${themePrefix}-${mapTheme || account.map_theme || "default"} 
    ${themePrefix}_filters-layout-${filterLayout || "insiderow"}
    ${themePrefix}_tags-layout-${tagsLayout || "dropdown"}
    ${themePrefix}_filters-position-${filtersPosition || "inside"}
    ${themePrefix}_content-position_d-${contentPositionDesktop || "left"}
    ${themePrefix}_content-position_m-${contentPositionMobile || "top"}
  `;
};

const getMapViewPort = (map) => {
  const newBounds = map.getBounds();
  const vp = {
    zoom: map.getZoom(),
    bounds: {
      nw_lng: newBounds.getSouthWest().lng(),
      se_lat: newBounds.getSouthWest().lat(),
      se_lng: newBounds.getNorthEast().lng(),
      nw_lat: newBounds.getNorthEast().lat(),
    },
  };
  return {
    ...vp,
  };
};

const timerFunc = (cb, seconds) => {
  if (seconds === 0) return cb();
  setTimeout(() => {
    cb();
  }, seconds * 1000);
};


const MapApp = (props) => {
  const { account, initedData = {}, currentGeo, setCurrentGeo, getLocale } = useAppContext();
  const {
    clusterEnabled,
    clusterSettings,
    defaultCenter,
    currentParams,
    containerRef,
    contentRef,
    mapViewRef,
    mapRef,
    geocoder, setGeocoder,
    autoSuggestion, setAutoSuggestion,
    filters, setFilters,
    isLoading, setIsLoading,
    isCollapsed, setIsCollapsed,
    openingFilters, setOpeningFilters,
    mapViewPort, setMapViewPort,
    selectedLocation, setSelectedLocation,
    hoveredLocation, setHoveredLocation,
    points, setPoints,
    clusters, supercluster
  } = useMixMap({});

  const { geo_type, enable_custom_params, gmap_api_key } = account;
  // console.log('MapApp filters', gmap_api_key, filters);

  const zoomAndMoveMap = useCallback((opts) => {
    if (opts.zoom) mapRef.current.setZoom(opts.zoom, true);
    timerFunc(
      () => {
        mapRef.current.panTo({
          lng: opts.lng,
          lat: opts.lat,
        });
        timerFunc(() => {
          mapRef.current.panBy(0, -50);
        }, 0);
      },
      opts.zoom ? 0.2 : 0
    );
  }, [mapRef]);

  const forceToCenter = useCallback((opts = {}) => {
    if (mapRef && mapRef.current && opts.lat && opts.lng) {
      zoomAndMoveMap(opts);
    } else {
      timerFunc(() => {
        if (mapRef.current) {
          zoomAndMoveMap(opts);
        }
      }, 2);
    }
  }, [zoomAndMoveMap]);

  const doFilters = useCallback(async (params, cb = null) => {
    setIsLoading(true);
    console.log('doFilters', params);
    lookupLocations(account.uid, params)
      .then(({ resources }) => {
        const newPoints = processLocations(resources);
          if (params && params.geo && params.geo.lat && params.geo.lng) {      
            setPoints(sortNearbyLocations(newPoints, params.geo.lat, params.geo.lng, true));
          } else {
            setPoints(newPoints);          
          }
        if (!filters.isFiltered) {
          setFilters({ ...filters, isFiltered: 1 });
        }
        setIsLoading(false);
        const isInside = detectPointsInBounds(mapViewPort, newPoints);
        if (!isInside && newPoints.length > 0) {
          forceToCenter({
            lat: +newPoints[0].properties.lat,
            lng: +newPoints[0].properties.lng,
          });
        }
        if (cb) cb(resources);
      })
      .catch((err) => {
        console.log("error", err);
        setIsLoading(false);
      });
  }, [account, mapViewPort, filters, forceToCenter]);

  useEffect(() => {
    const defaultParams = {};
    let initFiltering = true;
    if (!!initedData.selectedLocation) {
      defaultParams.location = initedData.selectedLocation;
    }
    if (!!currentParams.location) {
      defaultParams.location = currentParams.location;
    } else if (geo_type === "geo_browser_ip") {
      defaultParams.geo = filters.geo;
    } else if (geo_type === "disable") {
      defaultParams.all = 1;
    } else if (geo_type === "typing") {
      initFiltering = false;
    }
    if (!!initedData.selectedLocation || initFiltering) {
      doFilters({ ...defaultParams }, (resources) => {
        const loc = resources.find(
          (loc) =>
            loc.uid === currentParams.location ||
            loc.slug === currentParams.location
        );
        if (loc) {
          setFilters({...filters, isFiltered: true, search: loc.name});
        } else if (geo_type === "geo_browser_ip") {
          setFilters({ ...filters, isFiltered: true, search: currentGeo.address });
        }
      });
    }
  }, []);

  const initGeoData = async (newGeocoder) => {
    if (!currentParams.search || currentParams.search.length < 3) {
      if(geo_type === 'geo_browser_ip' && !currentGeo.address) {
        const currentLocation = await requestGeocode(newGeocoder, { location: {lat: currentGeo.lat, lng: currentGeo.lng} })
        if(currentLocation.results.length > 0) {
          setFilters({...filters, search: currentLocation.results[0].formatted_address});
          setCurrentGeo({...currentGeo, address: currentLocation.results[0].formatted_address});
        }
      }
      return null;
    }
    const {results } = await requestGeocode(newGeocoder, { address: currentParams.search })
    if (!results || results.length === 0) return null;
    setFilters({...filters, search: currentParams.search});

    const searchedLoc = results[0].geometry.location;
    const { lat, lng } = searchedLoc;
    const geo =
      typeof lat === "function" ? { lat: lat(), lng: lng() } : { lat, lng };

    const newFilters = {
      ...filters,
      geo: geo,
      search: currentParams.search
    };
    setFilters({
      ...newFilters,
    });
    // doFilters(newFilters);
    forceToCenter({
      ...geo,
      zoom: 17,
    });

  };

  const handleSelectLocation = (place) => {
    if (!place) return setSelectedLocation(null);

    setSelectedLocation(place);

    forceToCenter({
      lng: place.properties.lng,
      lat: place.properties.lat,
      zoom: Math.max(mapViewPort.zoom, CLUSTER_MAX_ZOOM + 1),
    });

    const { search, ...newParams } = currentParams;
    if (enable_custom_params) {
      syncLocationStates({
        ...newParams,
        location: place.properties.slug,
      })      
    }
    if (!initedData.preview) {
      CBAnalytics.click(account.uid, {
        location_name: place.properties.name,
        address: place.properties.full_address,
        location_id: place.properties.num,
        clicked_url: window.location.href,
      });
    }
  };

  const handleHoverLocation = (place) => {
    setHoveredLocation(place);
  };

  const apiIsLoaded = ({ map, maps, places }) => {
    mapRef.current = map;
    const newGeocoder = new window.google.maps.Geocoder();
    const autocomplete = new window.google.maps.places.AutocompleteService();
    setGeocoder(newGeocoder);
    setAutoSuggestion(autocomplete);
    setMapViewPort(getMapViewPort(map));
    initGeoData(newGeocoder);
    map.addListener("bounds_changed", () => {
      setMapViewPort(getMapViewPort(map));
    });
  };

  const handleClickCluster = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;

    const expansionZoom = Math.min(
      supercluster.getClusterExpansionZoom(cluster.id),
      CLUSTER_MAX_ZOOM + 1
    );
    mapRef.current.setZoom(expansionZoom, true);
    mapRef.current.panTo({ lat: latitude, lng: longitude });
  };

  const handleOpenFilters = (filterType) => {
    setOpeningFilters(filterType);
  };

  const handleChangeFilters = (ft) => {
    setFilters({ ...ft });
  };

  const handleSelectSearch = (data) => {
    const { lat, lng, location, ...existingFilters } = filters;
    const newFilters = {
      ...existingFilters,
      search: data.search,
      geo: {
        lat: data.lat ? data.lat : "",
        lng: data.lng ? data.lng : "",
      },
    };
    if(data.search && data.search.length > 0) {
      delete newFilters.country
      delete newFilters.province
      delete newFilters.city
      delete newFilters.all
    }
    if (data.location) {
      newFilters.location = data.location;
    }


    setFilters({ ...newFilters });
    doFilters({ ...newFilters });
    if (enable_custom_params && !data.lat && !data.lng) {
      const { location, search, ...newParams } = currentParams;
      syncLocationStates({...newParams});     
    }
  };

  const handleApplyFilters = useCallback((ft) => {
    if (ft) {
      const newFilters = {...filters, ...ft};
      if (ft && ft.all === 1) {
        delete newFilters.search; 
        delete newFilters.geo; 
      }    
      setFilters({ ...newFilters });
      doFilters({ ...newFilters });
    } else {
      doFilters({ ...filters });
    }
    setOpeningFilters(false);
    const { location, ...newParams } = currentParams;
    if (enable_custom_params) {
      syncLocationStates({...newParams});     
    }
  }, [currentParams, filters, enable_custom_params, doFilters]);

  const handleChangeMapBounds = ({ zoom, bounds }) => {
    const newBounds = {
      nw_lng: bounds.nw.lng,
      se_lat: bounds.se.lat,
      se_lng: bounds.se.lng,
      nw_lat: bounds.nw.lat,
    };
    setMapViewPort({
      zoom,
      bounds: { ...newBounds },
    });

    setFilters({
      ...filters,
      bounds: { ...newBounds },
    });
  };

  const handleToggleContent = (e) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
  };

  const dialog = account.dialog || {};
  const markers = clusterEnabled ? clusters : points;
  const showingDetails = dialog.detail_enabled && selectedLocation;
  const showListing = !showingDetails && !openingFilters;
  const isMobile = window.screen.availWidth < 767;
  const mapHeight = isMobile ? 700 : containerRef.current?.clientHeight;
  const maxContentHeight = mapHeight - mapViewRef.current?.clientHeight;

  return (
    <div className={appendClassName(account, initedData)}>
      <SearchOuter
        filters={filters}
        onChangeSetting={handleOpenFilters}
        onSelectSearch={handleSelectSearch}
        onChangeFilters={handleChangeFilters}
        onApplyFilters={handleApplyFilters}
        geocoder={geocoder}
        autoSuggestion={autoSuggestion}
      />
      <TaggableFilters
        filters={filters}
        onChangeFilters={handleChangeFilters}
        onApplyFilters={handleApplyFilters}
      />

      <div
        className="cbmap-wrapper"
        ref={containerRef}
        style={{
          height: !isMobile && initedData.widgetHeight ? initedData.widgetHeight : "",
        }}
      >
        <div
          className={`cbmap-content ${isCollapsed ? "is-collapsed" : ""}`}
          ref={contentRef}
          style={{
            height:
              isMobile && (isLoading || openingFilters)
                ? maxContentHeight
                : "",
          }}
        >
          <div
            className="cbmap-content-togglebar"
            onClick={handleToggleContent}
            dangerouslySetInnerHTML={{ __html: ICONS.CARET }}
            title={isCollapsed ? getLocale("show") : getLocale("hide")}
          />
          <Loading isLoading={isLoading} />
          <FilterDialog
            isOpen={!isLoading && openingFilters}
            filters={filters}
            filterType={openingFilters}
            onChangeFilters={handleChangeFilters}
            onApplyFilters={handleApplyFilters}
            onClose={(e) => setOpeningFilters(false)}
          />
          {showingDetails && (
            <LocationDialog
              isOpen={!!selectedLocation}
              item={selectedLocation}
              onClose={(e) => setSelectedLocation(null)}
            />
          )}

          <SearchInner
            isOpen={!isLoading && showListing}
            filters={filters}
            onChangeFilters={handleChangeFilters}
            onChangeSetting={handleOpenFilters}
            onSelectSearch={handleSelectSearch}
            onApplyFilters={handleApplyFilters}
            geocoder={geocoder}
            autoSuggestion={autoSuggestion}
          />
          {!isLoading && !showingDetails && !openingFilters && (
            <LeadForm
              filters={filters}
              results={points}
              onApplyFilters={handleApplyFilters}
            />
          )}

          <ListResults
            showListing={!isLoading && showListing}
            selectedLocation={selectedLocation}
            hoveredLocation={hoveredLocation}
            handleSelectLocation={handleSelectLocation}
            handleHoverLocation={handleHoverLocation}
            points={points}
            currentGeo={currentGeo}
          />
        </div>
        <div
          className="cbmap-mapview"
          ref={mapViewRef}
          style={{ height: mapHeight }}
        >
          <GoogleMapReact
            bootstrapURLKeys={{
              key: gmap_api_key,
              libraries: ["places", "geometry"],
            }}
            options={{
              styles: account.map_styles,
              animation: 2,
            }}
            defaultCenter={defaultCenter}
            defaultZoom={mapViewPort.zoom}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={apiIsLoaded}
            onChange={handleChangeMapBounds}
          >
            {currentGeo && currentGeo.lat && currentGeo.lng && (
              <GeoMarker lat={currentGeo.lat} lng={currentGeo.lng} />
            )}

            {markers.map((cluster) => {
              const {
                properties: { cluster: isCluster },
                geometry: {
                  coordinates: [longitude, latitude],
                },
              } = cluster;

              if (isCluster) {
                return (
                  <ClusterMarker
                    key={`cluster-${cluster.id}`}
                    lat={latitude}
                    lng={longitude}
                    settings={clusterSettings}
                    onClick={() => handleClickCluster(cluster)}
                    cluster={cluster}
                    points={points}
                  />
                );
              }

              return (
                <GMarker
                  key={`marker-${cluster.properties.uid}`}
                  lat={latitude}
                  lng={longitude}
                  place={cluster}
                  settings={clusterSettings}
                  currentGeo={currentGeo}
                  selectedLocation={selectedLocation}
                  hoveredLocation={hoveredLocation}                
                  onClose={(e) => handleSelectLocation(null)}
                  onSelectLocation={(e) => handleSelectLocation(cluster)}
                  onHoverLocation={(e) => handleHoverLocation(e)}
                />
              );
            })}
          </GoogleMapReact>
        </div>
      </div>
    </div>
  );
};

export default MapApp;
